import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import Image from "next/image";
import Select from "react-select";
import dynamic from "next/dynamic";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { ReCAPTCHA } from "@/lib/vendor";
import { Formik, Form, Field, ErrorMessage } from "formik";

import { PHONE_REGEX } from "@/utils/common";
const Button = dynamic(() => import("@/Components/Button"));
import { createPayPayPayment } from "@/services/paymentService";
import { hideFormLoader, showFormLoader } from "@/store/loaderSlice";
const FormInput = dynamic(() => import("../Home/Quote/QuoteForm/FormInput"));

interface QuoteFormValues {
  email: string;
  fullname: string;
  phone_number: string;
  company_name: string;
  services: string;
  currency: string;
  amount: string;
  message: string;
  confidential: boolean;
  ndaSigned: boolean;
  successUrl?: string;
  cancelUrl?: string;
  type_of_service?: string;
}

const initialValues: QuoteFormValues = {
  fullname: "",
  email: "",
  phone_number: "",
  company_name: "",
  services: "",
  currency: "",
  amount: "",
  message: "",
  confidential: true,
  ndaSigned: true,
};
// Custom error component for better accessibility
const ErrorMessageComponent = (props: any) => (
  <div className="text-red-500 font-medium text-xs mt-2" role="alert">
    {props.children}
  </div>
);
const services = [
  "Website pages",
  "Landing pages",
  "Email Template",
  "Blog",
  "Banner Ad",
  "Infographics",
  "Migration Service",
  "Other",
];
const validationSchema = Yup.object({
  fullname: Yup.string().trim().required("Full name is required."),
  email: Yup.string()
    .trim()
    .email("Email is not valid.")
    .max(100, "Email is too long")
    .required("Email is required.")
    .matches(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/, "Invalid email format"),
  phone_number: Yup.string()
    .nullable()
    .required("Phone is required")
    .matches(PHONE_REGEX, "Invalid phone number")
    .matches(/^[0-9]{10,15}$/, "Phone number must be between 10-15 digits"),
  company_name: Yup.string().required("Company name is required"),
  services: Yup.string().required("Please select a service"),
  currency: Yup.string().required("Please select a currency"),
  amount: Yup.number()
    .min(1)
    .label("Amount")
    .required("Amount is required")
    .positive("Amount must be positive"),
  message: Yup.string().required("Please enter a message"),
});
interface RequestFormProps {
  data?: any;
}

const RequestForm: React.FC<RequestFormProps> = ({ data }) => {
  const dispatch = useDispatch();
  const reCaptchaRef = useRef<ReCAPTCHA>(null);
  const [paymentUrl, setPaymentUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const sitekey = process.env.NEXT_PUBLIC_RECAPTCHA_V3;
  const appBaseUrl = process.env.NEXT_PUBLIC_REACT_APP_BASE_URL;

  useEffect(() => {
    if (paymentUrl !== "") window.location.href = paymentUrl;
  }, [paymentUrl]);

  const handleSubmit = async (
    values: QuoteFormValues,
    { resetForm, setSubmitting }: any,
  ) => {
    try {
      setIsLoading(true);
      dispatch(showFormLoader());

      values["successUrl"] = appBaseUrl + "success-payment";
      values["cancelUrl"] = appBaseUrl + "make-payment";
      values["type_of_service"] = values.services;

      try {
        await createPayPayPayment(values).then((res: any) => {
          if (!!res?.approvalUrl) {
            resetForm();
            setSubmitting(false);
            dispatch(hideFormLoader());
            setPaymentUrl(res.approvalUrl); // URL for user redirection
          } else {
            toast.error("Failed to initiate payment. Something went wrong");
          }
          return res;
        });
      } catch (error) {
        console.log("handleSubmit ~ error:", error);
        toast.error("Failed to initiate payment. Something went wrong");
        dispatch(hideFormLoader());
      }
    } catch (error) {
      console.log("handleSubmit ~ error:", error);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 5000);
    }
  };

  return (
    <div className="bg-[url(/images/Quote-bg.webp)]  bg-no-repeat bg-cover bg-center h-full w-full">
      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({ setFieldValue, values, errors, touched }) => (
          <Form
            className="p-[48px_24px] lg:p-[48px_24px] xl:p-[48px_48px] h-full items-stretch"
            aria-label="Request for Proposal Form"
          >
            <h5 className="text-heading-5 font-bold text-primary-text text-center mb-[24px]">
              Pay Securely Via Paypal
            </h5>

            <div>
              <div className="grid lg:grid-cols-2 gap-4 h-full items-stretch">
                <FormInput name="fullname" placeholder="Full name" required />
                <FormInput
                  required
                  name="email"
                  type="email"
                  placeholder="Email"
                />
                <FormInput
                  required
                  type="tel"
                  name="phone_number"
                  placeholder="Contact No"
                />
                <FormInput
                  required
                  name="company_name"
                  placeholder="Company Name"
                />

                <div className="sm:col-span-2 mb-[16px]">
                  <div className="input-group-select relative w-full cursor-pointer">
                    <Select
                      isSearchable={false}
                      placeholder="Select Services*"
                      className="w-full bg-white border border-border focus:border-text-button hover:border-text-button text-subtitle-3 font-regular text-secondary-text whitespace-normal text-sm placeholder:!text-[#040405]"
                      defaultValue={values.services}
                      onChange={(val: any) =>
                        setFieldValue("services", val.label)
                      }
                      options={services?.map((item: string) => ({
                        value: item,
                        label: item,
                      }))}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          border: "0",
                          borderRadius: "0",
                          padding: "11px  9px",
                          boxShadow: "none",
                          whiteSpace: "normal",
                          fontSize: "14px",
                        }),
                      }}
                    />
                  </div>
                  {errors.services && touched.services && (
                    <div
                      id="currency-error"
                      className="text-red-500 text-xs font-medium flex mt-2 mb-0 ml-1"
                      role="alert"
                    >
                      {errors.services}
                    </div>
                  )}
                </div>
                <FormInput
                  type="number"
                  required
                  name="amount"
                  placeholder="Enter Amount"
                />
                <div className="input-group-select relative w-full cursor-pointer">
                  <Select
                    isSearchable={false}
                    placeholder="Choose Currency"
                    className="w-full bg-white border border-border focus:border-text-button hover:border-text-button text-subtitle-3 font-regular text-secondary-text whitespace-normal text-sm placeholder:text-[#040405]"
                    defaultValue={values.currency}
                    onChange={(val: any) =>
                      setFieldValue("currency", val.label)
                    }
                    options={["USD", "EUR", "GBP", "AUD", "CAD"]?.map(
                      (item: string) => ({ value: item, label: item }),
                    )}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        border: "0",
                        borderRadius: "0",
                        padding: "11px  9px",
                        boxShadow: "none",
                        whiteSpace: "normal",
                        fontSize: "14px",
                      }),
                    }}
                  />
                  {errors.currency && touched.currency && (
                    <div
                      id="currency-error"
                      className="text-red-500 text-xs font-medium flex mt-2 mb-0 ml-1"
                      role="alert"
                    >
                      {errors.currency}
                    </div>
                  )}
                </div>
                <div className="textarea input-group sm:col-span-2 mb-0 ">
                  <Field
                    as="textarea"
                    name="message"
                    id="message"
                    className="input h-28 min-h-[120px]"
                    placeholder=" "
                    aria-label="Project description"
                    aria-describedby="message-error"
                    aria-required="true"
                  />
                  <label
                    htmlFor="message"
                    className="text-subtitle-5 font-regular"
                  >
                    Any Message...
                  </label>
                  <ErrorMessage
                    name="message"
                    component={ErrorMessageComponent}
                    id="message-error"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center md:justify-between justify-end md:flex-nowrap flex-wrap gap-6 mt-6">
              <div className="flex items-center gap-8 md:w-auto w-full">
                <div className="flex items-center gap-3">
                  <Image
                    src="/images/confidential.svg"
                    alt="confidential"
                    width={18}
                    height={18}
                  />
                  <span className="text-primary-text text-subtitle-4 font-regular">
                    100% confidential
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Image
                    src="/images/confidential.svg"
                    alt="confidential"
                    width={18}
                    height={18}
                  />
                  <span className="text-primary-text text-subtitle-4 font-regular">
                    We sign NDA
                  </span>
                </div>
              </div>
              <Button
                type="submit"
                variant="small"
                loading={isLoading}
                className="my-0 sm:w-fit w-full"
                label={data?.button_text || "Make Payment"}
              />
            </div>
            {sitekey && (
              <ReCAPTCHA
                style={{ display: "inline" }}
                theme="dark"
                size="invisible"
                ref={reCaptchaRef}
                sitekey={sitekey}
              />
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};
export default RequestForm;